import React, {useState} from "react";
import ContactInput from "./ContactInput";
import {BorderBottom, Call, Message, OpenMessage, UserIcon} from "../../../assets/icons";
import Button from "../../button/Button";
import {useLanguage} from "../../../hooks/LanguageContext";
import apiService from "../../../services/apiService";

const ContactUsForm = () => {
	const { direction, language } = useLanguage(); // Get language direction (ltr/rtl) and language

	// State to store form data, error messages, and submission status
	const [data, setData] = useState({
		name: "",
		email: "",
		phone: "",
		message: "",
	});

	const [errors, setErrors] = useState({
		name: "",
		email: "",
		phone: "",
		message: "",
	});

	const [statusMessage, setStatusMessage] = useState(""); // To display success or error messages
	const [isSubmitting, setIsSubmitting] = useState(false); // To disable submit button during submission

	// Handle form data changes
	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setData((prev) => ({ ...prev, [name]: value }));
	};


	// Validate form fields
	const validate = () => {
		const newErrors = {};
		setStatusMessage("")
		let isValid = true;

		// Check if name is empty
		if (!data.name) {
			newErrors.name = language === "ar" ? "الاسم مطلوب" : "Name is required";
			isValid = false;
		}

		// Check if email is invalid
		if ( data.email && !/\S+@\S+\.\S+/.test(data.email)) {
			newErrors.email = language === "ar" ? "البريد الالكتروني غير صالح" : "Email is invalid";
			isValid = false;
		}

		// Check if phone is empty
		if (!data.phone) {
			newErrors.phone = language === "ar" ? "رقم الجوال مطلوب" : "Phone number is required";
			isValid = false;
		}

		// Check if message is empty
		if (!data.message) {
			newErrors.message = language === "ar" ? "الرسالة مطلوبة" : "Message is required";
			isValid = false;
		}
		setErrors(newErrors);
		return isValid;
	};

	// Handle form submit
	const handleOnSubmit = async (event) => {
		event.preventDefault();

		// Validate the form before submitting
		if (validate()) {
			setIsSubmitting(true); // Disable submit button during submission
			try {
				// Send the form data using apiService
				const response = await apiService.sendContactUs(data);
				// Check if the response is successful
				if (response.status === 200) {
					// Reset form data and errors
					setData({
						name: "",
						email: "",
						phone: "",
						message: "",
					});
					setErrors({
						name: "",
						email: "",
						phone: "",
						message: "",
					});
					setStatusMessage(language === "ar" ? "تم إرسال الرسالة بنجاح!" : "Message sent successfully!");
				} else {
					setStatusMessage(language === "ar" ? "حدث خطأ أثناء إرسال الرسالة" : "An error occurred while sending the message.");
				}
			} catch (error) {
				setStatusMessage(language === "ar" ? "حدث خطأ أثناء إرسال الرسالة" : "An error occurred while sending the message.");
			} finally {
				setIsSubmitting(false); // Re-enable submit button
			}
		}
	};

	return (
		<form
			onSubmit={handleOnSubmit}
			className={`flex flex-col items-start w-full md:gap-[25px] gap-6 shadow-form-red-blur md:py-[50px] py-8 md:px-[20px] xl:px-[50px] px-8 ${
				direction === "rtl" ? "text-right" : "text-left"
			}`}
		>
			<div className="flex flex-col items-start gap-[30px]">
				<div className="flex flex-col items-start gap-4">
					<h3 className="headline-3-ar text-grayscale-600">
						{language === "ar" ? "املء بيانات النموذج بالاسفل..." : "Fill in the form below..."}
					</h3>
					<p
						className={`text-base leading-[23.52px] font-medium text-[#3B636B] ${
							direction === "rtl" ? "text-right" : "text-left"
						}`}
					>
						{language === "ar"
							? "قم بملء المعلومات في النموذج أدناه وسنتصل بك في أقرب وقت ممكن"
							: "Fill in the information in the form below, and we will contact you as soon as possible"}
					</p>
				</div>
				<BorderBottom className="max-w-full" />
			</div>

			{/* Name Input */}
			<div className="flex flex-col gap-y-1 w-full">
				<ContactInput
					type="text"
					name="name"
					placeholder={language === "ar" ? "الاسم" : "Name"}
					icon={<UserIcon className="w-[18px] h-[18px]" />}
					value={data.name}
					onInputChange={handleInputChange}
				/>
				{errors.name && <p className="text-primary-700 text-sm">{errors.name}</p>}
			</div>

			{/* Email Input */}
			<div className="flex flex-col gap-y-1 w-full">
				<ContactInput
					type="email"
					name="email"
					placeholder={language === "ar" ? "البريد الالكتروني" : "Email"}
					icon={<Message className="w-[18px] h-[18px]" />}
					value={data.email}
					onInputChange={handleInputChange}
				/>
				{errors.email && <p className="text-primary-700 text-sm">{errors.email}</p>}
			</div>

			{/* Phone Number Input */}
			<div className="flex flex-col gap-y-1 w-full">
				<ContactInput
					type="tel"
					name="phone"
					placeholder={language === "ar" ? "رقم الجوال" : "Phone Number"}
					icon={<Call className="w-[18px] h-[18px]" />}
					value={data.phone}
					onInputChange={handleInputChange}
				/>
				{errors.phone && <p className="text-primary-700 text-sm">{errors.phone}</p>}
			</div>

			{/* Message Textarea */}
			<div className="flex flex-col gap-y-1 w-full">
				<ContactInput
					type="textarea"
					name="message"
					placeholder={language === "ar" ? "الرسالة" : "Message"}
					icon={<OpenMessage className="w-[18px] h-[18px]" />}
					value={data.message}
					onInputChange={handleInputChange}
				/>
				{errors.message && <p className="text-primary-700 text-sm">{errors.message}</p>}
			</div>

			{/* Status Message */}
			{statusMessage && (
				<p
					className={`mt-4 text-center text-lg font-semibold ${
						statusMessage.includes("خطأ") || statusMessage.includes("error") ? "text-primary-700" : "text-green-500"
					}`}
				>
					{statusMessage}
				</p>
			)}

			{/* Submit Button */}
			<Button className="w-full rounded-[10px]" disabled={isSubmitting}>
				{isSubmitting ? (
					<div className="w-5 h-5 border-4 border-t-4 border-gray-200 border-t-primary-700 rounded-full animate-spin"></div>
				) : (
					<span>{language === "ar" ? "ارسال" : "Send"}</span> // Button text
				)}
			</Button>
		</form>
	);
};

export default ContactUsForm;
